/**
 * This file was auto-generated by client-types. Do not manually edit this file.
 * Your changes would be automatically overwritten.
 *
 * Service Documentation: https://private.hubteam.com/shoebill/service/com.hubspot.product.approvals.rpc.ApprovalsRpc
 */

import type {
  ChirpAvroRpcDetails,
  Requestify,
  unhandledEnumCase,
  Default,
  HubspotMetadata,
} from 'rpc-client-utils';

export type CausalChainLink = {
  coordinates: ChirpMethodCoordinates;
  errorClass: string;
  errorMessage?: string;
};

export type ChirpMethodCoordinates = { service: string; method: string };

export type ApprovalError =
  | 'UNKNOWN'
  | 'GATED'
  | 'INVALID_REQUEST'
  | 'NOT_FOUND'
  | 'APPROVAL_ALREADY_EXISTS'
  | 'TRANSITION_NOT_PERMITTED'
  | 'NOT_AUTHORIZED'
  | 'APPROVER_INVALID'
  | 'APPROVALS_NOT_ENABLED'
  | 'APPROVAL_TEMPLATE_CHANGED'
  | 'TOO_MANY_REQUESTS'
  | unhandledEnumCase;

export type Actor = { actorType: ActorType; id: string };

export type ActorType = 'USER' | 'AGENT' | 'TEAM' | unhandledEnumCase;

export type AgentTurnType = 'REVIEW' | 'APPLY_CHANGES' | unhandledEnumCase;

export type ApprovalAction =
  | 'PUBLISH'
  | 'MOVE_STAGE'
  | 'INSTALL'
  | 'EXECUTE'
  | unhandledEnumCase;

export type ApprovalConclusionReason =
  | 'ACTION_TAKEN'
  | 'APPROVALS_DISABLED'
  | 'REQUESTER_RECALLED_APPROVAL'
  | 'APPROVER_REJECTED'
  | unhandledEnumCase;

export type ApprovalRules =
  | 'NON_SEQUENTIAL'
  | 'ANY_CAN_APPROVE'
  | 'SEQUENTIAL'
  | unhandledEnumCase;

export type ApprovalStageRule = 'ALL' | 'ANY' | unhandledEnumCase;

export type ApprovalStatus =
  | 'APPROVAL_PENDING'
  | 'APPROVED'
  | 'CANCELLED'
  | 'REJECTED'
  | 'CHANGES_REQUESTED'
  | unhandledEnumCase;

export type ApprovalStepOperation =
  | 'APPROVE'
  | 'REQUEST_CHANGES'
  | 'REJECT'
  | unhandledEnumCase;

export type ApprovalStepStatus =
  | 'WAITING'
  | 'APPROVAL_PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CHANGES_REQUESTED'
  | unhandledEnumCase;

export type ApprovalChirpError = {
  errorType: ApprovalError;
  chirpErrorMessage?: string;
  coordinates?: ChirpMethodCoordinates;
  causalChain: Default<CausalChainLink[]>;
  shouldRetry: Default<boolean>;
};

export type ApproveApprovalRequest = { approvalId: number; note?: string };

export type ApproveApprovalStepRequest = {
  approvalStepId: number;
  note?: string;
};

export type CancelAgentTurnRequest = {
  approvalStepId: number;
  message?: string;
};

export type CancelApprovalRequest = { approvalId: number; note: string };

export type ConcludeApprovalRequest = {
  approvableObjectId: number;
  approvalTypeId: string;
  approvalAction: ApprovalAction;
  approvalConclusionReason: ApprovalConclusionReason;
};

export type CreateApprovalRequest = {
  approvalTypeId: string;
  approvalAction: ApprovalAction;
  approvableObjectId: number;
  approvalSteps: Default<CreateApprovalStepRequest[]>;
  approvalRules?: ApprovalRules;
  approvalStages: Default<CreateApprovalStageRequest[]>;
  title: string;
  requestNote?: string;
  dueDate?: number;
};

/**
 *  Completion rule for the steps sharing {@code order} on {@code ApprovalsRpc#createApproval}: ALL
 *  of them must approve, or ANY one of them. Orders without an entry use the approval's {@code
 *  approvalRules}. Deliberately separate from the internal and update stage requests so each
 *  endpoint's contract can evolve independently.
 */
export type CreateApprovalStageRequest = {
  order: number;
  rule: ApprovalStageRule;
};

export type CreateApprovalStepRequest = {
  actor: Actor;
  order?: number;
  shouldAutoApprove?: boolean;
};

export type CreateBulkApprovalRequest = {
  approvalRequests: Default<CreateApprovalRequest[]>;
};

export type GetApprovalRequest = { approvalId: number };

export type GetLatestApprovalRequest = {
  approvalTypeId: string;
  approvalAction: ApprovalAction;
  approvableObjectId: number;
};

export type MarkChangesResolvedRequest = {
  approvalStepId: number;
  message?: string;
};

export type RejectApprovalRequest = {
  approvalId: number;
  rejectionNote: string;
};

export type RejectApprovalStepRequest = {
  approvalStepId: number;
  rejectionNote: string;
};

export type RequestAgentToApplyChangesRequest = {
  approvalStepId: number;
  message?: string;
};

export type RequestAgentToReviewRequest = {
  approvalStepId: number;
  message?: string;
};

export type RequestChangesOnApprovalRequest = {
  approvalId: number;
  note?: string;
};

export type RequestChangesOnApprovalStepRequest = {
  approvalStepId: number;
  note?: string;
};

export type SendReminderNotificationRequest = { approvalStepId: number };

export type UpdateApprovalRequest = {
  approvalId: number;
  approvalSteps?: UpdateApprovalStepRequest[];
  dueDate?: number;
  title?: string;
  approvalStages?: UpdateApprovalStageRequest[];
};

/**
 *  Completion rule for the steps sharing {@code order} on {@code ApprovalsRpc#updateApproval}: ALL
 *  of them must approve, or ANY one of them. Deliberately separate from the create and internal
 *  stage requests so each endpoint's contract can evolve independently.
 */
export type UpdateApprovalStageRequest = {
  order: number;
  rule: ApprovalStageRule;
};

export type UpdateApprovalStepRequest = { actor: Actor; order?: number };

export type ApprovalResponse = {
  id: number;
  portalId: number;
  title: string;
  createDateTimestamp: number;
  approvalStatus: ApprovalStatus;
  approvalTypeId: string;
  approvalAction: ApprovalAction;
  approvalRules: ApprovalRules;
  requester: Actor;
  approvalSteps: Default<ApprovalStepResponse[]>;
  requestNote?: string;
  cancelNote?: string;
  cancelNoteTimestamp?: number;
  dueDate?: number;
  approvalConclusionReason?: ApprovalConclusionReason;
  approvalConcluded?: boolean;
  approvalTypeKey?: string;
  subjectId?: string;
};

export type ApprovalStepResponse = {
  id: number;
  approver: Actor;
  approvalStepStatus: ApprovalStepStatus;
  createDateTimestamp: number;
  approvedNote?: string;
  rejectionNote?: string;
  changesRequestedNote?: string;
  order?: number;
  supportsApplyChanges: Default<boolean>;
  inProgressTurnType?: AgentTurnType;
  conversationId?: number;
  reviewTurnId?: number;
  actedByUserId?: number;
  availableOperations: Default<ApprovalStepOperation[]>;
};

export type ApproveApprovalResponse = { approval: ApprovalResponse };

export type ApproveApprovalStepResponse = { approval: ApprovalResponse };

export type CancelAgentTurnResponse = {};

export type CancelApprovalResponse = { approval: ApprovalResponse };

export type ConcludeApprovalResponse = {};

export type CreateApprovalResponse = { approval: ApprovalResponse };

export type CreateBulkApprovalResponse = {
  createdApprovals: Default<ApprovalResponse[]>;
  failedRequests: Default<CreateApprovalRequest[]>;
};

export type GetApprovalResponse = { approval: ApprovalResponse };

export type GetLatestApprovalResponse = { approval?: ApprovalResponse };

export type MarkChangesResolvedResponse = { approval: ApprovalResponse };

export type RejectApprovalResponse = { approval: ApprovalResponse };

export type RejectApprovalStepResponse = { approval: ApprovalResponse };

export type RequestAgentToApplyChangesResponse = {};

export type RequestAgentToReviewResponse = {};

export type RequestChangesOnApprovalResponse = { approval: ApprovalResponse };

export type RequestChangesOnApprovalStepResponse = {
  approval: ApprovalResponse;
};

export type SendReminderNotificationResponse = {};

export type UpdateApprovalResponse = { approval: ApprovalResponse };

export type CreateApprovalRequestType = Requestify<CreateApprovalRequest>;
export type CreateApprovalResponseType = CreateApprovalResponse;
export type CreateApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const createApprovalRpc: ChirpAvroRpcDetails<
  CreateApprovalRequestType,
  CreateApprovalResponseType,
  CreateApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'createApproval',
  auth: 'app',
  fingerprint: '7MmYLwGCakx1A21iWu7eJIT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type CreateBulkApprovalRequestType =
  Requestify<CreateBulkApprovalRequest>;
export type CreateBulkApprovalResponseType = CreateBulkApprovalResponse;
export type CreateBulkApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const createBulkApprovalRpc: ChirpAvroRpcDetails<
  CreateBulkApprovalRequestType,
  CreateBulkApprovalResponseType,
  CreateBulkApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'createBulkApproval',
  auth: 'app',
  fingerprint: 'JSDgEpzYIhGBpwPnmdpx14T0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetLatestApprovalRequestType = Requestify<GetLatestApprovalRequest>;
export type GetLatestApprovalResponseType = GetLatestApprovalResponse;
export type GetLatestApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const getLatestApprovalRpc: ChirpAvroRpcDetails<
  GetLatestApprovalRequestType,
  GetLatestApprovalResponseType,
  GetLatestApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'getLatestApproval',
  auth: 'app',
  fingerprint: 'oVgYyyFd-qwSyZEDWL244oT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetApprovalRequestType = Requestify<GetApprovalRequest>;
export type GetApprovalResponseType = GetApprovalResponse;
export type GetApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const getApprovalRpc: ChirpAvroRpcDetails<
  GetApprovalRequestType,
  GetApprovalResponseType,
  GetApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'getApproval',
  auth: 'app',
  fingerprint: 'tO7k9L9wKAE3R6-phN7_0IT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type ApproveApprovalRequestType = Requestify<ApproveApprovalRequest>;
export type ApproveApprovalResponseType = ApproveApprovalResponse;
export type ApproveApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const approveApprovalRpc: ChirpAvroRpcDetails<
  ApproveApprovalRequestType,
  ApproveApprovalResponseType,
  ApproveApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'approveApproval',
  auth: 'app',
  fingerprint: 'w3LXpRbTttNW0ckj0pVAIIT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type ApproveApprovalStepRequestType =
  Requestify<ApproveApprovalStepRequest>;
export type ApproveApprovalStepResponseType = ApproveApprovalStepResponse;
export type ApproveApprovalStepErrorType = ApprovalChirpError & HubspotMetadata;
export const approveApprovalStepRpc: ChirpAvroRpcDetails<
  ApproveApprovalStepRequestType,
  ApproveApprovalStepResponseType,
  ApproveApprovalStepErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'approveApprovalStep',
  auth: 'app',
  fingerprint: '-GF6GyjPgYIlGCF0OfYYb4T0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type RejectApprovalRequestType = Requestify<RejectApprovalRequest>;
export type RejectApprovalResponseType = RejectApprovalResponse;
export type RejectApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const rejectApprovalRpc: ChirpAvroRpcDetails<
  RejectApprovalRequestType,
  RejectApprovalResponseType,
  RejectApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'rejectApproval',
  auth: 'app',
  fingerprint: 'TCiJepipaVPCuD-h9KH8UIT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type RejectApprovalStepRequestType =
  Requestify<RejectApprovalStepRequest>;
export type RejectApprovalStepResponseType = RejectApprovalStepResponse;
export type RejectApprovalStepErrorType = ApprovalChirpError & HubspotMetadata;
export const rejectApprovalStepRpc: ChirpAvroRpcDetails<
  RejectApprovalStepRequestType,
  RejectApprovalStepResponseType,
  RejectApprovalStepErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'rejectApprovalStep',
  auth: 'app',
  fingerprint: '5BOwN5YSTXRjZ3RZZLGDyYT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type RequestChangesOnApprovalRequestType =
  Requestify<RequestChangesOnApprovalRequest>;
export type RequestChangesOnApprovalResponseType =
  RequestChangesOnApprovalResponse;
export type RequestChangesOnApprovalErrorType = ApprovalChirpError &
  HubspotMetadata;
export const requestChangesOnApprovalRpc: ChirpAvroRpcDetails<
  RequestChangesOnApprovalRequestType,
  RequestChangesOnApprovalResponseType,
  RequestChangesOnApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'requestChangesOnApproval',
  auth: 'app',
  fingerprint: 'oyjmK_MVwfocZVqVc8TVSoT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type RequestChangesOnApprovalStepRequestType =
  Requestify<RequestChangesOnApprovalStepRequest>;
export type RequestChangesOnApprovalStepResponseType =
  RequestChangesOnApprovalStepResponse;
export type RequestChangesOnApprovalStepErrorType = ApprovalChirpError &
  HubspotMetadata;
export const requestChangesOnApprovalStepRpc: ChirpAvroRpcDetails<
  RequestChangesOnApprovalStepRequestType,
  RequestChangesOnApprovalStepResponseType,
  RequestChangesOnApprovalStepErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'requestChangesOnApprovalStep',
  auth: 'app',
  fingerprint: 'wgj03fpBv8ZOnaPq5aJ69YT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type MarkChangesResolvedRequestType =
  Requestify<MarkChangesResolvedRequest>;
export type MarkChangesResolvedResponseType = MarkChangesResolvedResponse;
export type MarkChangesResolvedErrorType = ApprovalChirpError & HubspotMetadata;
export const markChangesResolvedRpc: ChirpAvroRpcDetails<
  MarkChangesResolvedRequestType,
  MarkChangesResolvedResponseType,
  MarkChangesResolvedErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'markChangesResolved',
  auth: 'app',
  fingerprint: 'PSHl0Mk40-mmkWjXbXfnw4T0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type RequestAgentToApplyChangesRequestType =
  Requestify<RequestAgentToApplyChangesRequest>;
export type RequestAgentToApplyChangesResponseType =
  RequestAgentToApplyChangesResponse;
export type RequestAgentToApplyChangesErrorType = ApprovalChirpError &
  HubspotMetadata;
export const requestAgentToApplyChangesRpc: ChirpAvroRpcDetails<
  RequestAgentToApplyChangesRequestType,
  RequestAgentToApplyChangesResponseType,
  RequestAgentToApplyChangesErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'requestAgentToApplyChanges',
  auth: 'app',
  fingerprint: 'SZamX2qNXua9kx2Jj3hbCYT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type RequestAgentToReviewRequestType =
  Requestify<RequestAgentToReviewRequest>;
export type RequestAgentToReviewResponseType = RequestAgentToReviewResponse;
export type RequestAgentToReviewErrorType = ApprovalChirpError &
  HubspotMetadata;
export const requestAgentToReviewRpc: ChirpAvroRpcDetails<
  RequestAgentToReviewRequestType,
  RequestAgentToReviewResponseType,
  RequestAgentToReviewErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'requestAgentToReview',
  auth: 'app',
  fingerprint: 'gupRi2mXeYpoV53V6gXhSoT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type CancelAgentTurnRequestType = Requestify<CancelAgentTurnRequest>;
export type CancelAgentTurnResponseType = CancelAgentTurnResponse;
export type CancelAgentTurnErrorType = ApprovalChirpError & HubspotMetadata;
export const cancelAgentTurnRpc: ChirpAvroRpcDetails<
  CancelAgentTurnRequestType,
  CancelAgentTurnResponseType,
  CancelAgentTurnErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'cancelAgentTurn',
  auth: 'app',
  fingerprint: 'HKdvkEMvTjJEYRYcVjocnYT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type CancelApprovalRequestType = Requestify<CancelApprovalRequest>;
export type CancelApprovalResponseType = CancelApprovalResponse;
export type CancelApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const cancelApprovalRpc: ChirpAvroRpcDetails<
  CancelApprovalRequestType,
  CancelApprovalResponseType,
  CancelApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'cancelApproval',
  auth: 'app',
  fingerprint: 'HrDzSXNmqRsvYuBDb1PfWYT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type ConcludeApprovalRequestType = Requestify<ConcludeApprovalRequest>;
export type ConcludeApprovalResponseType = ConcludeApprovalResponse;
export type ConcludeApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const concludeApprovalRpc: ChirpAvroRpcDetails<
  ConcludeApprovalRequestType,
  ConcludeApprovalResponseType,
  ConcludeApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'concludeApproval',
  auth: 'app',
  fingerprint: 'tO-XK7M_hRVIRwLD1j28LIT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type UpdateApprovalRequestType = Requestify<UpdateApprovalRequest>;
export type UpdateApprovalResponseType = UpdateApprovalResponse;
export type UpdateApprovalErrorType = ApprovalChirpError & HubspotMetadata;
export const updateApprovalRpc: ChirpAvroRpcDetails<
  UpdateApprovalRequestType,
  UpdateApprovalResponseType,
  UpdateApprovalErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'updateApproval',
  auth: 'app',
  fingerprint: 'gDeQITkILFNdPuphC6qE-YT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type SendReminderNotificationRequestType =
  Requestify<SendReminderNotificationRequest>;
export type SendReminderNotificationResponseType =
  SendReminderNotificationResponse;
export type SendReminderNotificationErrorType = ApprovalChirpError &
  HubspotMetadata;
export const sendReminderNotificationRpc: ChirpAvroRpcDetails<
  SendReminderNotificationRequestType,
  SendReminderNotificationResponseType,
  SendReminderNotificationErrorType,
  false
> = {
  serviceName: 'com.hubspot.product.approvals.rpc.ApprovalsRpc',
  rpcName: 'sendReminderNotification',
  auth: 'app',
  fingerprint: 'CLm0NFVWppmLVdc5zf35iYT0rjFl7MXr',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};
